import { FC, useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { ActiveResp } from '../../types/activeResp';
import { Manifest } from '../../types/manifest';
import './adminpage.css';
//import { useAdminService } from '../../services/useAdminService';

const AdminPage: FC = () => {
    const serviceUrl = process.env.REACT_APP_SERVICEURL;
    const [data, setData] = useState<ActiveResp | null>(null);
    const [saving, setSaving] = useState(false);
    const [manifest, setManifest] = useState<Manifest | null>(null);
    //const { loadActive, reloadActive } = useAdminService();

    const load = useCallback(async () => {
        //const resp: AxiosResponse = await loadActive();
        const resp = await api.get(`${serviceUrl}/api/admin/getactive`);
        setData(resp.data);
        setManifest(resp.data.manifest);
    }, [serviceUrl]);

    const reload = async () => {
        await api.post(`${serviceUrl}/api/admin/reload`)
        await load();
    };

    const publish = async () => {
        if (!manifest) return;
        setSaving(true);
        await api.put(`${serviceUrl}/api/admin/updatemanifest`);
        setSaving(false);
        await load();
    };

    const upload = async (role: string, version: string, kind: "skills"|"quality", file: File) => {
        const form = new FormData();
        form.append("role", role);
        form.append("version", version);
        form.append("kind", kind);
        form.append("file", file);
        await api.post(`${serviceUrl}/api/admin/upload`, form, {
            headers: { "Content-Type": "multipart/form-data" }
        });
    };
    
    useEffect(() => { load(); }, [load]);

    if (!data || !manifest) return <div className='p-8'>Loading...</div>;

    return (
        <div>
            <h1 className="font-bold">Admin * Rule Management</h1>
            <section className="container manifest">
                <h2 className="font-semibold">Active Manifest</h2>
                <div className="manifest-grid">
                    <div>
                        <label>Current (global):</label>
                        <div style={{paddingTop: "3px"}}>
                            <input value={manifest.current} onChange={e => setManifest({ ...manifest, current: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label>Canary:</label>
                        <div style={{paddingTop: "3px"}}>
                            <input value={manifest.canary ?? ""} placeholder="optional" onChange={e => setManifest({ ...manifest, canary: e.target.value || null })} />
                        </div>
                    </div>
                    <div>
                        <label>Canary %:</label>
                        <div style={{paddingTop: "3px"}}>
                            <input type="number" min={0} max={50} value={manifest.canaryTrafficPct} onChange={e => setManifest({ ...manifest, canaryTrafficPct: Number(e.target.value) })} />
                        </div>
                    </div>
                    <div>
                        <div className="font-medium mb-2">Role versions:</div>
                        {Object.entries(manifest.roles).map(([role, ver]) => (
                            <div key={role} className="flex items-center gap-3 mb-2">
                                <span className="w-32">{role}</span>
                                <input className="border p-2 flex-1" value={ver} onChange={e => setManifest({ ...manifest, roles: { ...manifest.roles, [role]: e.target.value } })} />
                                <div>
                                    <span className="text-sm text-gray-500">
                                        available: {data.available[role]?.join(", ") || "—"}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button onClick={publish} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded">
                        {saving ? "Publishing…" : "Publish & Reload"}
                        </button>
                        <button onClick={reload} className="bg-gray-700 text-white px-4 py-2 rounded">Reload only</button>
                    </div>
                </div>
            </section>

            <section className="container">
                <h2 className="text-xl font-semibold">Upload Rules (per role/version)</h2>
                <UploadForm onUpload={upload} roles={Object.keys(data.available)} available={data.available} />
            </section>

            <section className="container">
                <h2 className="text-xl font-semibold">Loaded Snapshots</h2>
                <ul className="list-disc ml-6">
                {data.loaded.map(l => (
                    <li key={l.role}>{l.role}: {l.snapshot}</li>
                ))}
                </ul>
            </section>
        </div>
    );
}

function UploadForm({
    onUpload,
    roles,
    available
}: {
    onUpload: (role: string, version: string, kind: "skills"|"quality", file: File) => Promise<void>;
    roles: string[];
    available: Record<string, string[]>;
}) {
    const [role, setRole] = useState(roles[0] || "engineering");
    const [version, setVersion] = useState("");
    const [kind, setKind] = useState<"skills"|"quality">("skills");
    const [file, setFile] = useState<File | null>(null);

    return (
        <div className="flex flex-col gap-3 max-w-xl">
            <div className="flex gap-3">
                <select className="border p-2" value={role} onChange={e => setRole(e.target.value)}>
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input className="border p-2 flex-1" placeholder="version e.g. v0.2.3"
                value={version} onChange={e => setVersion(e.target.value)} />
                <select className="border p-2" value={kind} onChange={e => setKind(e.target.value as any)}>
                <option value="skills">skills_lexicon.json</option>
                <option value="quality">jd_quality_rules.json</option>
                </select>
            </div>
            <input type="file" accept=".json" onChange={e => setFile(e.target.files?.[0] || null)} />
            <button
                className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
                disabled={!role || !version || !file}
                onClick={async () => { if (file) await onUpload(role, version, kind, file); }}
            >
                Upload
            </button>
            <div className="text-sm text-gray-500">
                Existing versions for {role}: {available[role]?.join(", ") || "—"}
            </div>
        </div>
    );
}

export default AdminPage;